<template>
  <div style="min-height: calc(100vh - 176px)" class="flex flex-wrap mt-4">
    <div 
      class="relative py-2 px-2 flex flex-col min-w-0 break-words w-full mb-6 shadow-lg rounded-lg bg-blueGray-100 border-0" 
      style="min-height: calc(-176px + 100vh);"
    >
      <div class="bg-white shadow-md p-4 rounded-lg flex flex-wrap gap-4 mb-4">
        <input
          type="text"
          v-model="searchQuery"
          @input="updateFilters"
          placeholder="Поиск по имени эксперта..."
          class="border p-2 rounded w-full sm:w-1/3"
        />
        <select
          v-model="sortField"
          @change="updateFilters"
          class="border px-3 py-2 pr-7 rounded whitespace-nowrap"
        >
          <option value="user__fio">По ФИО {{ sortOrder === 'asc' ? '↑' : '↓' }}</option>
        </select>
        <button
          @click="toggleSortOrder"
          class="border px-3 py-2 rounded bg-gray-100 hover:bg-gray-200 whitespace-nowrap"
        >
          {{ sortOrder === 'asc' ? '↑ Возрастание' : '↓ Убывание' }}
        </button>
        <router-link
          to="/admin/experts/create"
          class="bg-blue-500 text-white px-4 py-2 rounded"
        >
          Создать эксперта
        </router-link>
      </div>

      <div class="bg-white shadow-md rounded-lg overflow-hidden">
        <table class="w-full text-left border-collapse">
          <thead>
            <tr class="bg-gray-200">
              <th class="p-3">#</th>
              <th class="p-3">Фото</th>
              <th class="p-3">ФИО</th>
              <th class="p-3">Email</th>
              <th class="p-3">Специализация</th>
              <th class="p-3">Пилот</th>
              <th class="p-3">Рейтинг</th>
              <th class="p-3">Курсы</th>
              <th class="p-3">Действия</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(expert, index) in expertsList" :key="expert.id" class="border-t">
              <td class="p-3">{{ index + 1 + (currentPage - 1) * perPage }}</td>
              <td class="p-3">
                <img v-if="expert.photo" :src="expert.photo" class="w-16 h-16 object-cover rounded" />
                <span v-else class="text-gray-500">Нет фото</span>
              </td>
              <td class="p-3">{{ expert.full_name }}</td>
              <td class="p-3">{{ expert.email }}</td>
              <td class="p-3">{{ expert.specialization || "Не указано" }}</td>
              <td class="p-3">
                <div v-if="expert.is_pilot" class="flex flex-col gap-1">
                  <span class="bg-blue-100 text-blue-800 text-xs font-semibold px-2 py-1 rounded">✓ Пилот</span>
                  <span class="text-xs text-gray-600">{{ getCategoryLabel(expert.pilot_category) }}</span>
                </div>
                <span
                  v-else-if="expert.is_pilot_pending_verification"
                  class="bg-yellow-100 text-yellow-800 text-xs font-bold px-2 py-1 rounded"
                >
                  Запрос на пилота
                </span>
                <span v-else class="text-gray-500 text-sm">—</span>
              </td>
              <td class="p-3">
                <span class="text-green-600">{{ expert.rating.toFixed(1) }} ⭐</span>
              </td>
              <td class="p-3">
                <div class="font-semibold">{{ expert.specialization || "Не указано" }}</div>
                <div v-if="expert.courses && expert.courses.length" class="text-xs text-gray-500 mt-1">
                  Ведёт:
                  <ul class="list-disc ml-4">
                    <li v-for="course in expert.courses" :key="course.id">
                      {{ course.title }}
                    </li>
                  </ul>
                </div>
              </td>
              <td class="p-3 flex gap-2 items-center">
                <router-link 
                  :to="`/admin/experts/edit/${expert.id}`" 
                  class="bg-blue-500 text-white px-3 py-1 rounded"
                >
                  Редактировать
                </router-link>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="flex justify-center mt-4">
        <button @click="changePage(currentPage - 1)" :disabled="currentPage === 1" class="px-4 py-2 bg-gray-300 rounded mr-2">
          ← Назад
        </button>
        <span class="px-4 py-2">Страница {{ currentPage }} из {{ totalPages }}</span>
        <button @click="changePage(currentPage + 1)" :disabled="currentPage >= totalPages" class="px-4 py-2 bg-gray-300 rounded ml-2">
          Вперед →
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { API_EXPERTS, HEADERS_TOKEN_JSON } from "@/const/uri";

export default {
  data() {
    return {
      expertsList: [],
      searchQuery: "",
      currentPage: 1,
      perPage: 10,
      totalPages: 1,
      sortField: "full_name",
      sortOrder: "asc",
    };
  },
  watch: {
    "$route.query": {
      immediate: true,
      handler() {
        this.fetchExperts();
      },
    },
  },
  mounted() {
    this.fetchExperts();
  },
  methods: {
    getCategoryLabel(category) {
      const labels = {
        first: 'Первая категория',
        second: 'Вторая категория',
        third: 'Третья категория',
        fourth: 'Четвёртая категория',
      };
      return labels[category] || category;
    },
    async fetchExperts() {
      const query = this.$route.query;

      this.searchQuery = query.search || "";
      this.currentPage = Number(query.page) || 1;
      this.sortField = query.sort || "user__fio";
      this.sortOrder = query.order || "asc";

      const orderingField = this.sortOrder === 'desc' ? `-${this.sortField}` : this.sortField;

      await axios({
        method: "GET",
        url: API_EXPERTS.list,
        params: {
          search: this.searchQuery,
          p: this.currentPage,
          page_size: this.perPage,
          ordering: orderingField,
        },
        headers: HEADERS_TOKEN_JSON(),
      })
        .then((e) => {
          if (e.data.code === 0) {
            const responseData = e.data.data;
            this.expertsList = responseData.results;
            this.totalPages = Math.ceil(responseData.count / this.perPage);
          }
        })
        .catch((e) => {
          console.error("Ошибка загрузки экспертов:", e);
        });
    },
    toggleSortOrder() {
      this.sortOrder = this.sortOrder === "asc" ? "desc" : "asc";
      this.updateFilters();
    },
    updateFilters() {
      this.$router.push({
        query: {
          search: this.searchQuery || undefined,
          sort: this.sortField,
          order: this.sortOrder,
          page: 1,
        },
      });
    },
    changePage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.$router.push({ query: { ...this.$route.query, page } });
      }
    },
  },
};
</script>
